import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box } from '@mui/material';
import type { GroupData } from '../../types/api.types';

interface EditGroupNameModalProps {
  open: boolean;
  group: GroupData;
  onClose: () => void;
  onRename: (group: GroupData, newName: string) => Promise<void>;
}

export const EditGroupNameModal: React.FC<EditGroupNameModalProps> = ({ open, group, onClose, onRename }) => {
  const [name, setName] = useState<string>(group.name);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state when the group changes or dialog opens
  useEffect(() => {
    if (open) {
      setName(group.name);
      setError(null);
      setLoading(false);
    }
  }, [open, group]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter a group name');
      return;
    }

    // If unchanged, just close
    if (trimmed === group.name) {
      onClose();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onRename(group, trimmed);
      // onRename success should handle snackbar and state; close here
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to rename group');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Group Name</DialogTitle>
      <form
        onSubmit={e => {
          e.preventDefault();
          if (!loading) handleSubmit();
        }}
      >
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Group Name"
              value={name}
              onChange={e => setName(e.target.value)}
              fullWidth
              required
              autoFocus
              disabled={loading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditGroupNameModal;
